import React, { useEffect, useRef } from 'react'

/**
 * HomeHeroField — the hero's spatial data-field.
 *
 * A slowly rotating pseudo-3D network of "captured data points" in the C3D
 * palette, wired by proximity links; a subset are brighter captured nodes with
 * a soft pulse. No twinkling starfield. A few slow data packets drift straight
 * down the field to draw the eye down the page.
 *
 * Dependency-free canvas 2D. Respects prefers-reduced-motion (single static
 * frame). Pauses when offscreen or the tab is hidden. aria-hidden.
 */
type Tint = 'purple' | 'blue' | 'teal' | 'multi'
interface HomeHeroFieldProps {
  /** Tint the field to a section's colour. Defaults to the multi-hue mix. */
  tint?: Tint
  /** Spread the point-field wider (closer to the edges) on wide sections. */
  wide?: boolean
  /**
   * Global motion multiplier for the rotation + drifting packets. Defaults
   * to 1 (the homepage/CTA feel). Pass a smaller value (e.g. 0.2) for a
   * near-still, very subtle field.
   */
  speed?: number
  /**
   * The bright cyan "data packets" that drift down the field. They're the
   * most eye-catching element, so pass false on pages where the field must
   * stay quiet behind foreground content (e.g. a form).
   */
  packets?: boolean
}

const HomeHeroField: React.FC<HomeHeroFieldProps> = ({
  tint = 'multi',
  wide = false,
  speed = 1,
  packets: showPackets = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const parent = canvas?.parentElement
    const ctx = canvas?.getContext('2d')
    if (!canvas || !parent || !ctx) return

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // On small screens / touch devices, don't run the animation loop or attach
    // a mousemove listener — draw a single static frame instead. Keeps phones
    // from spinning up multiple rAF loops + global listeners for a background.
    const lowPower =
      window.matchMedia('(max-width: 767px)').matches ||
      window.matchMedia('(pointer: coarse)').matches
    const still = reduce || lowPower
    const DPR = Math.min(window.devicePixelRatio || 1, 2)
    // Node palette + link colour follow the section tint so each CTA's
    // constellation matches the section it belongs to.
    const PALETTES: Record<Tint, number[][]> = {
      purple: [
        [164, 67, 240],
        [190, 120, 250],
        [132, 68, 216],
      ],
      blue: [
        [0, 135, 246],
        [86, 178, 250],
        [40, 110, 220],
      ],
      teal: [
        [9, 186, 184],
        [64, 210, 205],
        [0, 150, 150],
      ],
      multi: [
        [164, 67, 240],
        [0, 135, 246],
        [0, 201, 199],
      ],
    }
    const COLORS = PALETTES[tint]
    const LINE = {
      purple: '176,120,240',
      blue: '110,170,245',
      teal: '90,200,200',
      multi: '140,160,255',
    }[tint]

    let w = 0
    let h = 0
    type Node = {
      x: number
      y: number
      z: number
      c: number[]
      captured: boolean
      phase: number
    }
    const nodes: Node[] = []
    // Slow "data packets" that drift straight DOWN the field, drawing the eye
    // down the page. Screen-space (not tied to nodes) so the motion reads as a
    // clean vertical current.
    type Packet = { x: number; y: number; sp: number; r: number }
    let packets: Packet[] = []

    const build = () => {
      const rect = parent.getBoundingClientRect()
      w = rect.width
      h = rect.height
      canvas.width = Math.round(w * DPR)
      canvas.height = Math.round(h * DPR)
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0)

      // Data-point network — restrained, clean geometric shapes
      // Node count drives an O(n^2) link check, so keep it modest for perf;
      // the large `scale` below still gives a wide, spread-out field.
      const target = Math.max(44, Math.min(78, Math.round(w / 16)))
      if (nodes.length !== target) {
        nodes.length = 0
        for (let i = 0; i < target; i++) {
          nodes.push({
            x: Math.random() * 2 - 1,
            y: Math.random() * 2 - 1,
            z: Math.random() * 2 - 1,
            c: COLORS[i % COLORS.length],
            captured: Math.random() < 0.16,
            phase: Math.random() * Math.PI * 2,
          })
        }
      }

      // Vertical data packets — a few, spread across the width, staggered
      const pc = Math.max(3, Math.round(w / 260))
      packets = []
      for (let i = 0; i < pc; i++) {
        packets.push({
          x: Math.random() * w,
          y: Math.random() * h,
          sp: 0.16 + Math.random() * 0.2,
          r: 1.5 + Math.random() * 0.8,
        })
      }
    }
    build()

    let t = 0
    let angle = 0.3
    let mx = 0
    let my = 0
    let tmx = 0
    let tmy = 0
    const onMove = (e: MouseEvent) => {
      tmx = e.clientX / window.innerWidth - 0.5
      tmy = e.clientY / window.innerHeight - 0.5
    }

    const draw = () => {
      // `speed` scales EVERY moving part, not just the rotation: `t` drives the
      // captured-node pulse and the lerp factor drives pointer parallax. When
      // only the rotation was scaled, those two still ran at full rate and the
      // field read as fast even at speed 0.2.
      t += 0.016 * speed
      mx += (tmx - mx) * 0.05 * speed
      my += (tmy - my) * 0.05 * speed
      ctx.clearRect(0, 0, w, h)

      // Rotating data-point network (the shapes). The ANIMATED axis is pitch
      // (rotation about X), so the field tumbles vertically — the motion reads
      // as flowing DOWN the page, not sweeping left/right. The mouse adds only
      // a small yaw/parallax.
      const yaw = mx * 0.5
      const cyaw = Math.cos(yaw)
      const syaw = Math.sin(yaw)
      const cp = Math.cos(angle)
      const sp = Math.sin(angle)
      // Wide sections spread the field toward the edges (based on width);
      // otherwise the balanced min-dimension spread.
      const scale = wide
        ? Math.max(Math.min(w, h) * 0.62, w * 0.46)
        : Math.min(w, h) * 0.62
      const proj = nodes.map(n => {
        // yaw about Y (x,z) — mouse parallax only
        const rx = n.x * cyaw - n.z * syaw
        let rz = n.x * syaw + n.z * cyaw
        // pitch about X (y,z) — the animated, downward-reading axis
        const ry = n.y * cp - rz * sp
        rz = n.y * sp + rz * cp
        const persp = 2.2 / (2.7 + rz)
        return {
          n,
          px: w / 2 + (rx + mx * 0.3) * scale * persp,
          py: h / 2 + (ry + my * 0.3) * scale * persp,
          depth: (rz + 1) / 2,
        }
      })

      for (let i = 0; i < proj.length; i++) {
        for (let j = i + 1; j < proj.length; j++) {
          const dx = proj[i].px - proj[j].px
          const dy = proj[i].py - proj[j].py
          const d = Math.hypot(dx, dy)
          if (d < 152) {
            ctx.strokeStyle = `rgba(${LINE},${(1 - d / 152) * 0.17})`
            ctx.lineWidth = 1
            ctx.beginPath()
            ctx.moveTo(proj[i].px, proj[i].py)
            ctx.lineTo(proj[j].px, proj[j].py)
            ctx.stroke()
          }
        }
      }

      for (const q of proj) {
        const [r, g, b] = q.n.c
        if (q.n.captured) {
          // A captured data point: brighter, with a soft pulsing ring
          const pulse = 0.5 + 0.5 * Math.sin(t * 1.6 + q.n.phase)
          const rad = 1.8 + q.depth * 2.6
          ctx.beginPath()
          ctx.fillStyle = `rgba(${r},${g},${b},0.12)`
          ctx.arc(q.px, q.py, rad * (3 + pulse * 2.4), 0, Math.PI * 2)
          ctx.fill()
          ctx.beginPath()
          ctx.fillStyle = `rgba(${r},${g},${b},${0.55 + q.depth * 0.4})`
          ctx.arc(q.px, q.py, rad, 0, Math.PI * 2)
          ctx.fill()
        } else {
          const rad = 0.9 + q.depth * 2
          ctx.beginPath()
          ctx.fillStyle = `rgba(${r},${g},${b},${0.32 + q.depth * 0.46})`
          ctx.arc(q.px, q.py, rad, 0, Math.PI * 2)
          ctx.fill()
        }
      }

      // Vertical data packets drifting down the field
      for (const pk of showPackets ? packets : []) {
        pk.y += pk.sp * speed
        if (pk.y > h + 24) {
          pk.y = -24
          pk.x = Math.random() * w
        }
        // faint upward trail
        const trail = ctx.createLinearGradient(pk.x, pk.y - 46, pk.x, pk.y)
        trail.addColorStop(0, 'rgba(150,225,255,0)')
        trail.addColorStop(1, 'rgba(150,225,255,0.22)')
        ctx.strokeStyle = trail
        ctx.lineWidth = 1.2
        ctx.beginPath()
        ctx.moveTo(pk.x, pk.y - 46)
        ctx.lineTo(pk.x, pk.y)
        ctx.stroke()
        // soft halo + bright head
        ctx.beginPath()
        ctx.fillStyle = 'rgba(150,225,255,0.14)'
        ctx.arc(pk.x, pk.y, pk.r * 3.4, 0, Math.PI * 2)
        ctx.fill()
        ctx.beginPath()
        ctx.fillStyle = 'rgba(180,238,255,0.95)'
        ctx.arc(pk.x, pk.y, pk.r, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    let raf = 0
    let running = false
    const loop = () => {
      angle += 0.0006 * speed
      draw()
      raf = requestAnimationFrame(loop)
    }
    const start = () => {
      if (running || still) return
      running = true
      raf = requestAnimationFrame(loop)
    }
    const stop = () => {
      running = false
      cancelAnimationFrame(raf)
    }

    const onResize = () => {
      build()
      draw()
    }
    window.addEventListener('resize', onResize)
    if (!still) window.addEventListener('mousemove', onMove)

    const onVis = () => (document.hidden ? stop() : start())
    document.addEventListener('visibilitychange', onVis)

    const io = new IntersectionObserver(
      entries => entries.forEach(e => (e.isIntersecting ? start() : stop())),
      { threshold: 0 },
    )
    io.observe(canvas)

    draw()
    if (!still) start()

    return () => {
      stop()
      io.disconnect()
      window.removeEventListener('resize', onResize)
      window.removeEventListener('mousemove', onMove)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [tint, wide, speed, showPackets])

  return <canvas ref={canvasRef} className="hero-field" aria-hidden="true" />
}

export default HomeHeroField
